import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  AlertCircle,
  TrendingUp,
  Flag,
  CreditCard,
  Users,
  FileText,
  Menu,
  X,
  LogOut,
  ShieldAlert,
  UserCog,
  Settings,
} from 'lucide-react';
import { useState } from 'react';
import { usePlatformAdminAuthStore } from '../stores/platform-admin-auth.store';

const navigation = [
  { name: 'Dashboard', href: '/platform-admin', icon: LayoutDashboard },
  { name: 'Organizations', href: '/platform-admin/organizations', icon: Building2 },
  { name: 'System Events', href: '/platform-admin/events', icon: AlertCircle },
  { name: 'Usage Analytics', href: '/platform-admin/usage', icon: TrendingUp },
  { name: 'Feature Flags', href: '/platform-admin/feature-flags', icon: Flag },
  { name: 'Billing', href: '/platform-admin/billing', icon: CreditCard },
  { name: 'Batch Operations', href: '/platform-admin/batch', icon: Users },
  { name: 'Admin Users', href: '/platform-admin/admin-users', icon: UserCog },
  { name: 'Audit Logs', href: '/platform-admin/audit', icon: FileText },
];

export default function PlatformAdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { admin, logout } = usePlatformAdminAuthStore();

  const handleLogout = () => {
    logout();
    navigate('/platform-admin/login');
  };

  return (
    <div className="h-screen flex overflow-hidden bg-orange-50">
      {/* Warning Banner - Always Visible */}
      <div className="fixed top-0 left-0 right-0 bg-gradient-to-r from-orange-600 to-red-600 text-white px-4 py-2 z-50 shadow-lg">
        <div className="flex items-center justify-center gap-2 text-sm font-medium">
          <ShieldAlert className="h-4 w-4" />
          <span>PLATFORM ADMIN MODE - Business Owner Access</span>
        </div>
      </div>

      {/* Mobile sidebar */}
      <div
        className={`fixed inset-0 flex z-40 lg:hidden ${
          sidebarOpen ? '' : 'pointer-events-none'
        }`}
        style={{ marginTop: '40px' }}
      >
        <div
          className={`fixed inset-0 bg-gray-600 transition-opacity ${
            sidebarOpen ? 'opacity-75' : 'opacity-0'
          }`}
          onClick={() => setSidebarOpen(false)}
        />

        <div
          className={`relative flex-1 flex flex-col max-w-xs w-full bg-white border-r-4 border-orange-500 transform transition-transform ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-6 w-6 text-white" />
            </button>
          </div>

          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            <div className="flex-shrink-0 flex items-center px-4 gap-3">
              <ShieldAlert className="h-8 w-8 text-orange-600" />
              <h1 className="text-xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                Platform Admin
              </h1>
            </div>
            <nav className="mt-5 px-2 space-y-1">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                      isActive
                        ? 'bg-orange-100 text-orange-900'
                        : 'text-gray-700 hover:bg-orange-50 hover:text-orange-900'
                    }`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <item.icon
                      className={`mr-3 h-5 w-5 ${
                        isActive ? 'text-orange-600' : 'text-gray-500 group-hover:text-orange-600'
                      }`}
                    />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* User Section */}
          <div className="flex-shrink-0 border-t border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <Link
                to="/platform-admin/profile"
                className="flex items-center flex-1 hover:bg-gray-50 rounded-lg p-2 transition-colors"
                onClick={() => setSidebarOpen(false)}
              >
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center text-white font-semibold">
                    {admin?.firstName?.[0]}{admin?.lastName?.[0]}
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-700">
                    {admin?.firstName} {admin?.lastName}
                  </p>
                  <p className="text-xs text-gray-500">{admin?.role}</p>
                </div>
              </Link>
              <button
                onClick={handleLogout}
                className="p-2 text-gray-400 hover:text-orange-600"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
            <Link
              to="/platform-admin/profile"
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
              onClick={() => setSidebarOpen(false)}
            >
              <Settings className="h-4 w-4" />
              <span>Profile & Settings</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:flex-shrink-0" style={{ marginTop: '40px' }}>
        <div className="flex flex-col w-64 border-r-4 border-orange-500 bg-white">
          <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
            <div className="flex items-center flex-shrink-0 px-4 gap-3">
              <ShieldAlert className="h-8 w-8 text-orange-600" />
              <h1 className="text-xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                Platform Admin
              </h1>
            </div>
            <nav className="mt-5 flex-1 px-2 space-y-1">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                      isActive
                        ? 'bg-orange-100 text-orange-900'
                        : 'text-gray-700 hover:bg-orange-50 hover:text-orange-900'
                    }`}
                  >
                    <item.icon
                      className={`mr-3 h-5 w-5 ${
                        isActive ? 'text-orange-600' : 'text-gray-500 group-hover:text-orange-600'
                      }`}
                    />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* User Section */}
          <div className="flex-shrink-0 border-t border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <Link
                to="/platform-admin/profile"
                className="flex items-center flex-1 hover:bg-gray-50 rounded-lg p-2 transition-colors"
              >
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center text-white font-semibold text-sm">
                    {admin?.firstName?.[0]}{admin?.lastName?.[0]}
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-700">
                    {admin?.firstName} {admin?.lastName}
                  </p>
                  <p className="text-xs text-gray-500">{admin?.role}</p>
                </div>
              </Link>
              <button
                onClick={handleLogout}
                className="p-2 text-gray-400 hover:text-orange-600 transition-colors"
                title="Logout"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
            <Link
              to="/platform-admin/profile"
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
            >
              <Settings className="h-4 w-4" />
              <span>Profile & Settings</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden" style={{ marginTop: '40px' }}>
        <div className="lg:hidden">
          <div className="flex items-center justify-between bg-white border-b border-gray-200 px-4 py-2">
            <button
              className="text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-orange-500"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-6 w-6" />
            </button>
            <span className="text-sm font-medium text-gray-700">
              {navigation.find(item => item.href === location.pathname)?.name || 'Platform Admin'}
            </span>
            <div className="w-6" /> {/* Spacer for alignment */}
          </div>
        </div>

        <main className="flex-1 relative overflow-y-auto focus:outline-none bg-orange-50">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
